import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, Polyline, useJsApiLoader } from "@react-google-maps/api";
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock3,
  Columns3,
  FileText,
  History,
  Loader2,
  MapPin,
  Navigation,
  RefreshCw,
  Search,
  Settings2,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useSelector } from "react-redux";
import {
  getTimecardReportAudit,
  getTimecardReportNotes,
  getTimecardReportViolations,
  runTimecardReport,
} from "../../network/reportsApi";
import { getDepartments, getSites } from "../../redux/features/common";
import { RootState, useAppDispatch } from "../../redux/store";
import {
  DepartmentMaster,
  ReportColumn,
  ReportData,
  ReportFilter,
  ReportRow,
  SiteType,
  TimecardAuditDetail,
  TimecardNoteDetail,
  TimecardReportParams,
  TimecardViolationDetail,
} from "../../types";
import { TrackingSessionService } from "../../services/firebase/trackingSessionService";
import { LocationPoint } from "../../types/model/trackingSessionTypes";
import DateTimeUtil from "../../utils/dateTime";

const PAGE_LIMIT = 20;
const DASH = "—";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
const TRACKING_MAP_CONTAINER = { width: "100%", height: "100%" };
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";
const DEFAULT_REPORT_FILTERS: ReportFilter[] = [
  { field: "date_range", label: "Date range", input_type: "date_range", required: true },
  { field: "site_id", label: "Site", input_type: "select", required: false },
  { field: "department_code", label: "Department", input_type: "multi_select", required: false },
  { field: "search", label: "Search employee", input_type: "text", required: false },
];

type DraftFilters = {
  date_from: string;
  date_to: string;
  site_id: string;
  department_codes: string[];
  search: string;
};

type Option = {
  value: string;
  label: string;
};

type DetailKind = "notes" | "audit" | "violations" | "tracking";

type ActiveDetail = {
  kind: DetailKind;
  row: ReportRow;
  rowKey: string;
} | null;

type DetailCacheStatus = "loading" | "loaded" | "error";

type DetailCacheValue = {
  status: DetailCacheStatus;
  loading: boolean;
  error: string | null;
  notes?: TimecardNoteDetail[];
  audit?: TimecardAuditDetail[];
  violations?: TimecardViolationDetail[];
  trackingPoints?: LocationPoint[];
};

const today = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().split("T")[0];
};

const emptyFilters = (): DraftFilters => ({
  date_from: today(),
  date_to: today(),
  site_id: "",
  department_codes: [],
  search: "",
});

function normalizeText(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getString(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function getNumber(row: ReportRow, keys: string[]): number {
  for (const key of keys) {
    const raw = row[key];
    if (raw !== null && raw !== undefined && raw !== "") {
      const value = Number(raw);
      if (!Number.isNaN(value)) return value;
    }
  }
  return 0;
}

function hasColumn(columns: ReportColumn[], key: string, visibility: Record<string, boolean>): boolean {
  const column = columns.find((item) => item.key === key);
  if (!column) return false;
  return !column.toggleable || visibility[key] !== false;
}

function formatDate(value: unknown, withYear = true): string {
  if (typeof value !== "string" || !value) return DASH;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

function formatDateRange(start: string, end: string): string {
  if (!start || !end) return "Select dates";
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return `${start} - ${end}`;
  return `${startDate.toLocaleDateString("en-US", { month: "short", day: "2-digit" })} - ${endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })}`;
}

function formatTime(value: unknown): string {
  return getString(value) || DASH;
}

function formatHours(value: unknown): string {
  if (value === null || value === undefined || value === "") return "0h";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return `${value}`;
  return `${numberValue.toFixed(2)} hrs`;
}

function formatCompactHours(value: unknown): string {
  if (value === null || value === undefined || value === "") return "0h";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return `${value}`;
  return `${numberValue.toFixed(numberValue % 1 === 0 ? 0 : 2)}h`;
}

function formatCellValue(value: unknown, column?: ReportColumn): string {
  if (value === null || value === undefined || value === "") return DASH;
  if (column?.type === "date") return formatDate(value);
  if (typeof value === "number") {
    if (column?.type === "duration" || column?.type === "number") return value.toFixed(2);
    return String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : DASH;
  if (typeof value === "object") return DASH;
  return String(value);
}

function formatDetailValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") return DASH;
  if (typeof value === "number") return Number.isInteger(value) ? value : value.toFixed(2);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : DASH;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function parseJsonObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function getInitials(name: string): string {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  return initials || "TC";
}

function rowKey(row: ReportRow, index: number): string {
  return [
    row.os_emp_schedules_id,
    row.employee_id,
    row.employee,
    row.date,
    row.clock_in,
    row.clock_out,
    index,
  ]
    .filter((part) => part !== null && part !== undefined && part !== "")
    .join("-");
}

const RECORD_ID_KEYS = [
  "os_emp_schedules_id",
  "time_entry_id",
  "timecard_id",
  "record_id",
  "punch_id",
  "os_emp_timecard_id",
  "id",
];

function getRecordId(row: ReportRow): string | number | null {
  const probe = (bag: Record<string, unknown>): string | number | null => {
    for (const key of RECORD_ID_KEYS) {
      const value = bag[key];
      if (value !== null && value !== undefined && value !== "") return value as string | number;
    }
    return null;
  };

  return probe(row) ?? probe((row.expand ?? {}) as Record<string, unknown>);
}

function getEmployeeId(row: ReportRow): string | number | undefined {
  const value = row.employee_id ?? row.os_emp_account_id;
  if (value === null || value === undefined || value === "") return undefined;
  return value as string | number;
}

function getTrackingUuid(row: ReportRow): string {
  return getString(row.tracking_uuid);
}

function getBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
}

function isGeoTrackingAvailable(row: ReportRow): boolean {
  const explicit = row.geo_tracking_available;
  if (explicit !== null && explicit !== undefined && explicit !== "") return getBoolean(explicit);
  return getBoolean(row.geo_tracking);
}

function hasGeoTrackingColumn(columns: ReportColumn[], visibility: Record<string, boolean>): boolean {
  return hasColumn(columns, "geo_tracking_available", visibility) || hasColumn(columns, "geo_tracking", visibility);
}

function getTotalHours(row: ReportRow, metricKey?: string): number {
  const explicitTotal = getNumber(row, ["total_hours", "hours"]);
  if (explicitTotal) return explicitTotal;
  const metric = metricKey ? Number(row[metricKey]) : NaN;
  if (!Number.isNaN(metric) && metric > 0) return metric;
  return getNumber(row, ["regular"]) + getNumber(row, ["ot"]) + getNumber(row, ["dt"]);
}

function mapSitesToOptions(sites: SiteType[]): Option[] {
  return sites
    .map((site) => ({
      value: String(site.us_user_site_profile_id ?? ""),
      label: site.user_site_name ?? site.us_site_code ?? "Unnamed site",
    }))
    .filter((option) => option.value);
}

function mapDepartmentsToOptions(departments: DepartmentMaster[]): Option[] {
  return departments
    .map((department) => ({
      value: department.department_code ?? department.code ?? "",
      label: department.department_name ?? department.name ?? department.department_code ?? "Unnamed department",
    }))
    .filter((option) => option.value);
}

function detailCacheKey(rowKeyValue: string, kind: DetailKind): string {
  return `${rowKeyValue}:${kind}`;
}

function getDetailTitle(kind: DetailKind): string {
  switch (kind) {
    case "notes":
      return "Notes";
    case "audit":
      return "Audit History";
    case "violations":
      return "Geo Violations";
    case "tracking":
      return "Geo Tracking";
    default:
      return "Details";
  }
}

function isMobileAccordionNumericDetail(key: string, value: unknown): boolean {
  const numericDetailKeys = new Set(["regular", "ot", "dt", "geo_violation_count", "violation_count", "notes_audit_count", "notes_count", "audit_count"]);
  if (numericDetailKeys.has(key)) return true;
  if (key.endsWith("_count")) return true;
  if (typeof value === "number") return true;
  if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim())) return true;
  return false;
}

function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`;
}

// True only when a value is worth rendering - keeps label-with-em-dash rows out of the UI.
function hasDetailValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed !== "" && trimmed !== DASH;
  }
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
}

// Mirrors ExpandStrip's field list (including its `expand.x ?? row.x` fallback) so the mobile
// accordion and the desktop strip agree, then appends any other expand keys, dropping empties.
function getMobileDetailEntries(row: ReportRow): Array<[string, unknown]> {
  const expand = (row.expand ?? {}) as Record<string, unknown>;
  const preferred: Array<[string, unknown]> = [
    ["Department", expand.department ?? row.department],
    ["Site", expand.site ?? row.site],
    ["Work Code", expand.work_code ?? row.work_code],
    ["Break", expand.break ?? expand.break_time ?? row.break_time],
    ["Scheduled", expand.scheduled ?? row.scheduled],
    ["Scheduled vs Actual", expand.actual_vs_scheduled ?? row.actual_vs_scheduled],
    ["Rule Applied", expand.rule_applied ?? row.rule_applied],
    ["Latest Note", expand.latest_note ?? expand.note ?? expand.notes_latest],
  ];

  const consumedKeys = new Set([
    "department",
    "site",
    "work_code",
    "break",
    "break_time",
    "scheduled",
    "actual_vs_scheduled",
    "rule_applied",
    "latest_note",
    "note",
    "notes_latest",
  ]);

  // Internal flags already represented by the action row (Geo/Track buttons and their
  // counts/tones) - raw text here would just repeat that information.
  const suppressedKeys = new Set([
    "geo_status",
    "geo_tracking",
    "geo_tracking_available",
    "notes_count",
    "audit_count",
    "notes_audit_count",
    "violation_count",
    "geo_violation_count",
    "tracking_uuid",
  ]);

  const additional = Object.entries(expand).filter(
    ([key, value]) => !consumedKeys.has(key) && !suppressedKeys.has(key) && !isMobileAccordionNumericDetail(key, value)
  );

  return [...preferred, ...additional.map(([key, value]): [string, unknown] => [normalizeText(key), value])].filter(
    ([, value]) => hasDetailValue(value)
  );
}

// Only rendered once the data is actually loaded, so it never flashes a count during
// loading or contradicts an error state.
function getDetailSummary(kind: DetailKind, cache: DetailCacheValue | undefined): string | null {
  if (cache?.status !== "loaded") return null;

  switch (kind) {
    case "notes":
      return pluralize(cache.notes?.length ?? 0, "note");
    case "audit":
      return pluralize(cache.audit?.length ?? 0, "event");
    case "violations":
      return pluralize(cache.violations?.length ?? 0, "violation");
    case "tracking": {
      const validPoints = (cache.trackingPoints ?? []).filter(
        (point) => isFiniteNumber(point.latitude) && isFiniteNumber(point.longitude)
      );
      return pluralize(validPoints.length, "point");
    }
    default:
      return null;
  }
}

const TRACKING_TIME_FORMAT = "MMM D, YYYY hh:mm:ss A";

// DateTimeUtil.formatWithDayjs returns the literal "Invalid date" for empty input, and
// dayjs itself returns "Invalid Date" for malformed input - treat both as a miss.
function formatDateTimeValue(value: Date | string | null | undefined, dateFormat = TRACKING_TIME_FORMAT): string | null {
  if (!value) return null;
  const formatted = DateTimeUtil.formatWithDayjs(value, dateFormat);
  return formatted.includes("Invalid") ? null : formatted;
}

function formatTrackingPointTime(point: LocationPoint): string {
  // Device-local wall-clock string: formatWithDayjs uses plain dayjs(), so no timezone shift.
  const local = formatDateTimeValue(point.recorded_at_local);
  if (local) return local;

  // ISO/UTC string from the sync layer -> rendered in the viewer's local zone.
  const recorded = formatDateTimeValue(point.recorded_at);
  if (recorded) return recorded;

  // Numeric epoch; Firebase writes milliseconds, but tolerate seconds.
  if (isFiniteNumber(point.timestamp) && point.timestamp > 0) {
    const millis = point.timestamp < 1e12 ? point.timestamp * 1000 : point.timestamp;
    const stamped = formatDateTimeValue(new Date(millis));
    if (stamped) return stamped;
  }

  return DASH;
}

// Re-evaluates on resize/orientation change, unlike a bare matchMedia() call.
function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_MEDIA_QUERY).matches
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

function buildMapsUrl(lat: string | number, lng: string | number): string {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isAndroid) return `geo:${lat},${lng}?q=${lat},${lng}`;
  if (isIOS) return `maps://maps.apple.com/?q=${lat},${lng}`;
  return `https://maps.google.com/?q=${lat},${lng}`;
}

function formatCoordinate(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return DASH;
  return value.toFixed(6);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const radius = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getTrackingDistanceKm(points: LocationPoint[]): number {
  return points.reduce((distance, point, index) => {
    if (index === 0) return distance;
    const previous = points[index - 1];
    if (![previous.latitude, previous.longitude, point.latitude, point.longitude].every(isFiniteNumber)) return distance;
    return distance + haversineMeters(previous.latitude, previous.longitude, point.latitude, point.longitude);
  }, 0) / 1000;
}

const LoadingState: React.FC = () => (
  <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      Loading timecard report
    </div>
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-800">
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold">Cannot load this report</h2>
        <p className="mt-1 text-sm">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-red-200 bg-white px-3 text-sm font-medium text-red-700 hover:bg-red-100"
      >
        <RefreshCw className="h-4 w-4" />
        Retry
      </button>
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
    <div>
      <CalendarDays className="mx-auto h-9 w-9 text-gray-400" />
      <h2 className="mt-3 text-base font-semibold text-gray-900">No timecard records found</h2>
      <p className="mt-1 text-sm text-gray-500">Try a wider date range or fewer filters.</p>
    </div>
  </div>
);

const UnsupportedLayoutState: React.FC<{ layout: string }> = ({ layout }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-800">
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div>
        <h2 className="text-base font-semibold">Unsupported report layout</h2>
        <p className="mt-1 text-sm">This page can render table reports, but the API returned {layout}.</p>
      </div>
    </div>
  </div>
);

const EmployeeCell: React.FC<{ row: ReportRow }> = ({ row }) => {
  const name = getString(row.employee) || "Employee";
  const imageUrl = getString(row.image_url ?? row.employee_image ?? row.avatar_url);
  const employeeCode = getString(row.employee_code ?? row.employee_id);

  return (
    <div className="flex items-center gap-3">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
          {getInitials(name)}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate font-semibold text-gray-950">{name}</div>
        {employeeCode && (
          <div className="mt-0.5 text-xs text-gray-500">ID: {employeeCode}</div>
        )}
      </div>
    </div>
  );
};

const ActionButton: React.FC<{
  label: string;
  count?: number;
  tone: "blue" | "purple" | "orange" | "green";
  active?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ label, count, tone, active, disabled, icon, onClick }) => {
  const tones = {
    blue: "text-blue-700 bg-blue-50 border-blue-100",
    purple: "text-purple-700 bg-purple-50 border-purple-100",
    orange: "text-orange-700 bg-orange-50 border-orange-100",
    green: "text-green-700 bg-green-50 border-green-100",
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`inline-flex min-w-[72px] items-center justify-center gap-2 rounded-md border px-2.5 py-2 text-sm font-semibold transition ${
        tones[tone]
      } ${active ? "ring-2 ring-blue-200" : ""} ${disabled ? "cursor-not-allowed opacity-45" : "hover:shadow-sm"}`}
      aria-label={label}
      title={label}
    >
      {icon}
      {count !== undefined && <span className="text-gray-950">{count}</span>}
      <span className="sr-only">{label}</span>
    </button>
  );
};

const MobileActionButton: React.FC<{
  label: string;
  count?: number;
  tone: "blue" | "purple" | "orange" | "green";
  disabled?: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ label, count, tone, disabled, icon, onClick }) => {
  const tones = {
    blue: "text-blue-700",
    purple: "text-purple-700",
    orange: "text-orange-700",
    green: "text-green-700",
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1 border-r border-gray-100 px-2 text-xs font-semibold last:border-r-0 ${
        disabled ? "cursor-not-allowed opacity-45" : "active:bg-gray-50"
      }`}
      aria-label={label}
    >
      <span className={tones[tone]}>{icon}</span>
      <span className="text-gray-950">{count ?? ""}</span>
      <span className="font-medium text-gray-600">{label}</span>
    </button>
  );
};

const ColumnsPanel: React.FC<{
  columns: ReportColumn[];
  visibility: Record<string, boolean>;
  align?: "left" | "right";
  iconOnly?: boolean;
  onToggle: (key: string) => void;
}> = ({ columns, visibility, align = "right", iconOnly, onToggle }) => {
  const [open, setOpen] = useState(false);
  const toggleableColumns = columns.filter((column) => column.toggleable);

  if (!toggleableColumns.length) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
        aria-label="Columns"
      >
        <Columns3 className="h-4 w-4" />
        {!iconOnly && "Columns"}
      </button>
      {open && (
        <div className={`absolute z-20 mt-2 max-h-80 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-xl ${align === "right" ? "right-0" : "left-0"}`}>
          {toggleableColumns.map((column) => (
            <label key={column.key} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <input
                type="checkbox"
                checked={visibility[column.key] ?? column.visible}
                onChange={() => onToggle(column.key)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>{column.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportFilters: React.FC<{
  filters: ReportFilter[];
  draftFilters: DraftFilters;
  siteOptions: Option[];
  departmentOptions: Option[];
  compact?: boolean;
  loading: boolean;
  validationMessage: string | null;
  onDraftChange: (filters: DraftFilters) => void;
  onSubmit: () => void;
}> = ({ filters, draftFilters, siteOptions, departmentOptions, compact, loading, validationMessage, onDraftChange, onSubmit }) => {
  const [departmentPanelOpen, setDepartmentPanelOpen] = useState(false);
  const update = (patch: Partial<DraftFilters>) => onDraftChange({ ...draftFilters, ...patch });

  const controlClass = "h-10 rounded-md border border-gray-300 bg-white text-sm text-gray-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

  const renderFilter = (filter: ReportFilter) => {
    if (filter.input_type === "date_range") {
      return (
        <div key={filter.field} className={`flex items-center gap-2 ${controlClass} px-3 ${compact ? "w-full" : "min-w-[300px]"}`}>
          <CalendarDays className="h-4 w-4 shrink-0 text-gray-500" />
          <input
            type="date"
            value={draftFilters.date_from}
            onChange={(event) => update({ date_from: event.target.value })}
            className="min-w-0 flex-1 border-none bg-transparent text-sm outline-none"
            aria-label={`${filter.label} start`}
          />
          <span className="text-gray-400">-</span>
          <input
            type="date"
            value={draftFilters.date_to}
            onChange={(event) => update({ date_to: event.target.value })}
            className="min-w-0 flex-1 border-none bg-transparent text-sm outline-none"
            aria-label={`${filter.label} end`}
          />
        </div>
      );
    }

    if (filter.input_type === "select") {
      return (
        <label key={filter.field} className={`relative ${compact ? "w-full" : "min-w-[170px]"}`}>
          <span className="sr-only">{filter.label}</span>
          <select
            value={draftFilters.site_id}
            onChange={(event) => update({ site_id: event.target.value })}
            className={`${controlClass} w-full appearance-none px-3 pr-9`}
          >
            <option value="">All Sites</option>
            {siteOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        </label>
      );
    }

    if (filter.input_type === "multi_select") {
      return (
        <div key={filter.field} className={`relative ${compact ? "w-full" : "min-w-[190px]"}`}>
          <button
            type="button"
            onClick={() => setDepartmentPanelOpen((open) => !open)}
            className={`${controlClass} flex w-full items-center justify-between gap-2 px-3`}
          >
            <span className="truncate">{draftFilters.department_codes.length ? `${draftFilters.department_codes.length} Departments` : "All Departments"}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
          </button>
          {departmentPanelOpen && (
            <div className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
              {departmentOptions.length ? (
                departmentOptions.map((option) => {
                  const checked = draftFilters.department_codes.includes(option.value);
                  return (
                    <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? draftFilters.department_codes.filter((code) => code !== option.value)
                            : [...draftFilters.department_codes, option.value];
                          update({ department_codes: next });
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="min-w-0 truncate">{option.label}</span>
                    </label>
                  );
                })
              ) : (
                <div className="px-2 py-2 text-sm text-gray-500">No departments available</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <label key={filter.field} className={`relative ${compact ? "w-full" : "min-w-[230px]"}`}>
        <span className="sr-only">{filter.label}</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={draftFilters.search}
          onChange={(event) => update({ search: event.target.value })}
          placeholder={filter.label}
          className={`${controlClass} w-full pl-9 pr-3`}
        />
      </label>
    );
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-3"}>
      <div className={`flex ${compact ? "flex-col" : "flex-wrap items-center"} gap-3`}>
        {filters.map(renderFilter)}
        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          className={`${compact ? "w-full" : ""} inline-flex h-10 items-center justify-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Apply
        </button>
      </div>
      {validationMessage && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {validationMessage}
        </div>
      )}
    </div>
  );
};

const DesktopHeader: React.FC<{
  report: ReportData | null;
  draftFilters: DraftFilters;
  filters: ReportFilter[];
  siteOptions: Option[];
  departmentOptions: Option[];
  columnVisibility: Record<string, boolean>;
  loading: boolean;
  validationMessage: string | null;
  onDraftChange: (filters: DraftFilters) => void;
  onSubmit: () => void;
  onToggleColumn: (key: string) => void;
}> = ({ report, draftFilters, filters, siteOptions, departmentOptions, columnVisibility, loading, validationMessage, onDraftChange, onSubmit, onToggleColumn }) => (
  <div className="hidden md:block">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-950">Timecard Report</h1>
        <div className="mt-1 flex items-center gap-2 text-sm font-medium text-gray-600">
          <span>{formatDateRange(draftFilters.date_from, draftFilters.date_to)}</span>
          <CalendarDays className="h-4 w-4 text-gray-500" />
        </div>
      </div>
      <button
        type="button"
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm"
        aria-label="Report settings"
      >
        <Settings2 className="h-4 w-4" />
      </button>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      {filters.length ? (
        <ReportFilters
          filters={filters}
          draftFilters={draftFilters}
          siteOptions={siteOptions}
          departmentOptions={departmentOptions}
          loading={loading}
          validationMessage={validationMessage}
          onDraftChange={onDraftChange}
          onSubmit={onSubmit}
        />
      ) : (
        <div className="text-sm text-gray-500">Preparing report filters</div>
      )}
      {report && <ColumnsPanel columns={report.columns} visibility={columnVisibility} onToggle={onToggleColumn} />}
    </div>
  </div>
);

const MobileHeader: React.FC<{
  report: ReportData | null;
  draftFilters: DraftFilters;
  filters: ReportFilter[];
  siteOptions: Option[];
  departmentOptions: Option[];
  columnVisibility: Record<string, boolean>;
  filterOpen: boolean;
  loading: boolean;
  validationMessage: string | null;
  onDraftChange: (filters: DraftFilters) => void;
  onSubmit: () => void;
  onToggleColumn: (key: string) => void;
  onToggleFilter: () => void;
}> = ({ report, draftFilters, filters, siteOptions, departmentOptions, columnVisibility, filterOpen, loading, validationMessage, onDraftChange, onSubmit, onToggleColumn, onToggleFilter }) => (
  <div className="md:hidden">
    <div className="sticky top-0 z-10 -mx-4 mb-3 border-b border-gray-100 bg-white px-4 pb-3 pt-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-bold text-gray-950">Timecard Report</h1>
        <button type="button" onClick={onToggleFilter} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-900" aria-label="Filters">
          <SlidersHorizontal className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onToggleFilter}
          className="flex h-10 min-w-0 flex-1 items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-900"
        >
          <span className="truncate">{formatDateRange(draftFilters.date_from, draftFilters.date_to)}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-blue-600" />
        </button>
        <button type="button" onClick={onToggleFilter} className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700" aria-label="Date filters">
          <CalendarDays className="h-4 w-4" />
        </button>
        {report && <ColumnsPanel columns={report.columns} visibility={columnVisibility} align="right" iconOnly onToggle={onToggleColumn} />}
      </div>
    </div>
    {filterOpen && (
      <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <ReportFilters
          filters={filters}
          draftFilters={draftFilters}
          siteOptions={siteOptions}
          departmentOptions={departmentOptions}
          compact
          loading={loading}
          validationMessage={validationMessage}
          onDraftChange={onDraftChange}
          onSubmit={onSubmit}
        />
      </div>
    )}
  </div>
);

const ExpandStrip: React.FC<{
  row: ReportRow;
  rowKeyValue: string;
  activeDetail: ActiveDetail;
  onOpenDetail: (kind: DetailKind, row: ReportRow, rowKeyValue: string) => void;
}> = ({ row, rowKeyValue, activeDetail, onOpenDetail }) => {
  const expand = row.expand ?? {};
  const notesCount = getNumber(row, ["notes_count", "notes_audit_count"]);
  const auditCount = getNumber(row, ["audit_count", "notes_audit_count"]);
  const violationCount = getNumber(row, ["violation_count", "geo_violation_count"]);
  const geoTrackingAvailable = isGeoTrackingAvailable(row);
  const items: Array<[string, unknown]> = [
    ["Department", expand.department ?? row.department],
    ["Site", expand.site ?? row.site],
    ["Work Code", expand.work_code ?? row.work_code],
    ["Break", expand.break ?? expand.break_time ?? row.break_time],
    ["Schedule", expand.scheduled ?? row.scheduled],
    ["Scheduled vs Actual", expand.actual_vs_scheduled ?? row.actual_vs_scheduled],
    ["Rule Applied", expand.rule_applied ?? row.rule_applied],
  ];
  const latestNote = expand.latest_note ?? expand.note ?? expand.notes_latest;

  return (
    <div className="rounded-md border border-blue-100 bg-blue-50/40 p-4 shadow-inner">
      <div className="grid gap-4 lg:grid-cols-8">
        {items.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <div className="text-xs font-medium text-gray-500">{label}</div>
            <div className={`mt-1 truncate text-sm font-semibold ${label === "Scheduled vs Actual" && getString(value).startsWith("-") ? "text-red-600" : "text-gray-950"}`}>
              {formatDetailValue(value)}
            </div>
          </div>
        ))}
        <div className="min-w-0 lg:col-span-2">
          <div className="text-xs font-medium text-gray-500">Notes (Latest)</div>
          <div className="mt-1 line-clamp-2 text-sm font-medium text-gray-950">{formatDetailValue(latestNote)}</div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 border-t border-blue-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
        <ActionButton
          label="Notes"
          count={notesCount}
          tone="blue"
          active={activeDetail?.kind === "notes" && activeDetail.rowKey === rowKeyValue}
          icon={<ClipboardList className="h-4 w-4" />}
          onClick={() => onOpenDetail("notes", row, rowKeyValue)}
        />
        <ActionButton
          label="Audit"
          count={auditCount}
          tone="purple"
          active={activeDetail?.kind === "audit" && activeDetail.rowKey === rowKeyValue}
          icon={<Clock3 className="h-4 w-4" />}
          onClick={() => onOpenDetail("audit", row, rowKeyValue)}
        />
        <ActionButton
          label="Geo Violations"
          count={violationCount}
          tone={violationCount > 0 ? "orange" : "green"}
          active={activeDetail?.kind === "violations" && activeDetail.rowKey === rowKeyValue}
          icon={<AlertTriangle className="h-4 w-4" />}
          onClick={() => onOpenDetail("violations", row, rowKeyValue)}
        />
        <ActionButton
          label="Geo Tracking"
          tone="green"
          disabled={!geoTrackingAvailable}
          active={activeDetail?.kind === "tracking" && activeDetail.rowKey === rowKeyValue}
          icon={<MapPin className="h-4 w-4" />}
          onClick={() => onOpenDetail("tracking", row, rowKeyValue)}
        />
      </div>
    </div>
  );
};

const DesktopTable: React.FC<{
  report: ReportData;
  columnVisibility: Record<string, boolean>;
  expandedRows: Set<string>;
  activeDetail: ActiveDetail;
  onToggleRow: (key: string) => void;
  onOpenDetail: (kind: DetailKind, row: ReportRow, rowKeyValue: string) => void;
}> = ({ report, columnVisibility, expandedRows, activeDetail, onToggleRow, onOpenDetail }) => {
  const columns = report.columns;
  const showEmployee = hasColumn(columns, "employee", columnVisibility);
  const showDate = hasColumn(columns, "date", columnVisibility);
  const showClock = hasColumn(columns, "clock_in", columnVisibility) || hasColumn(columns, "clock_out", columnVisibility);
  const showHours = ["regular", "ot", "dt"].some((key) => hasColumn(columns, key, columnVisibility));
  const showTotal = showHours || hasColumn(columns, "total_hours", columnVisibility);
  const showNotes = hasColumn(columns, "notes_count", columnVisibility) || hasColumn(columns, "notes_audit_count", columnVisibility);
  const showAudit = hasColumn(columns, "audit_count", columnVisibility) || hasColumn(columns, "notes_audit_count", columnVisibility);
  const showViolations = hasColumn(columns, "violation_count", columnVisibility) || hasColumn(columns, "geo_violation_count", columnVisibility);
  const showTracking = hasGeoTrackingColumn(columns, columnVisibility);

  return (
    <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:block">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr className="text-xs font-bold text-gray-950">
              <th className="w-10 px-4 py-3 text-left" />
              {showEmployee && <th className="px-4 py-3 text-left">Employee</th>}
              {showDate && <th className="px-4 py-3 text-left">Date</th>}
              {showClock && <th className="px-4 py-3 text-left">In / Out</th>}
              {showHours && <th className="px-4 py-3 text-left">Regular / OT / DT</th>}
              {showTotal && <th className="px-4 py-3 text-left">Total Hours</th>}
              {showNotes && <th className="px-4 py-3 text-center">Notes</th>}
              {showAudit && <th className="px-4 py-3 text-center">Audit</th>}
              {showViolations && <th className="px-4 py-3 text-center">Geo Violation</th>}
              {showTracking && <th className="px-4 py-3 text-center">Geo Tracking</th>}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {report.rows.map((row, index) => {
              const key = rowKey(row, index);
              const expanded = expandedRows.has(key);
              const notesCount = getNumber(row, ["notes_count", "notes_audit_count"]);
              const auditCount = getNumber(row, ["audit_count", "notes_audit_count"]);
              const violationCount = getNumber(row, ["violation_count", "geo_violation_count"]);
              const geoTrackingAvailable = isGeoTrackingAvailable(row);
              const totalHours = getTotalHours(row, report.mobile.metric);

              return (
                <React.Fragment key={key}>
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 align-middle">
                      <button
                        type="button"
                        onClick={() => onToggleRow(key)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        aria-label={expanded ? "Collapse row" : "Expand row"}
                      >
                        <ChevronRight className={`h-5 w-5 transition-transform ${expanded ? "rotate-90" : ""}`} />
                      </button>
                    </td>
                    {showEmployee && (
                      <td className="min-w-[210px] px-4 py-3 align-middle">
                        <EmployeeCell row={row} />
                      </td>
                    )}
                    {showDate && <td className="min-w-[150px] px-4 py-3 align-middle text-gray-700">{formatDate(row.date)}</td>}
                    {showClock && (
                      <td className="min-w-[120px] px-4 py-3 align-middle text-gray-800">
                        <div>{formatTime(row.clock_in)}</div>
                        <div className="mt-1">{formatTime(row.clock_out)}</div>
                      </td>
                    )}
                    {showHours && (
                      <td className="min-w-[160px] px-4 py-3 align-middle text-gray-700">
                        <div>Regular&nbsp; {formatCompactHours(row.regular)}</div>
                        <div className="mt-1">OT&nbsp; {formatCompactHours(row.ot)} <span className="mx-1 text-gray-300">•</span> DT&nbsp; {formatCompactHours(row.dt)}</div>
                      </td>
                    )}
                    {showTotal && <td className="min-w-[110px] px-4 py-3 align-middle font-bold text-gray-950">{formatHours(totalHours)}</td>}
                    {showNotes && (
                      <td className="px-4 py-3 text-center align-middle">
                        <ActionButton
                          label="Notes"
                          count={notesCount}
                          tone="blue"
                          active={activeDetail?.kind === "notes" && activeDetail.rowKey === key}
                          icon={<ClipboardList className="h-4 w-4" />}
                          onClick={() => onOpenDetail("notes", row, key)}
                        />
                      </td>
                    )}
                    {showAudit && (
                      <td className="px-4 py-3 text-center align-middle">
                        <ActionButton
                          label="Audit"
                          count={auditCount}
                          tone="purple"
                          active={activeDetail?.kind === "audit" && activeDetail.rowKey === key}
                          icon={<Clock3 className="h-4 w-4" />}
                          onClick={() => onOpenDetail("audit", row, key)}
                        />
                      </td>
                    )}
                    {showViolations && (
                      <td className="px-4 py-3 text-center align-middle">
                        <ActionButton
                          label="Geo Violations"
                          count={violationCount}
                          tone={violationCount > 0 ? "orange" : "green"}
                          active={activeDetail?.kind === "violations" && activeDetail.rowKey === key}
                          icon={<AlertTriangle className="h-4 w-4" />}
                          onClick={() => onOpenDetail("violations", row, key)}
                        />
                      </td>
                    )}
                    {showTracking && (
                      <td className="px-4 py-3 text-center align-middle">
                        <ActionButton
                          label="Geo Tracking"
                          tone="green"
                          disabled={!geoTrackingAvailable}
                          active={activeDetail?.kind === "tracking" && activeDetail.rowKey === key}
                          icon={<MapPin className="h-4 w-4" />}
                          onClick={() => onOpenDetail("tracking", row, key)}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-right align-middle">
                      <button
                        type="button"
                        onClick={() => onToggleRow(key)}
                        className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                      >
                        More details
                        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={12} className="bg-white px-4 py-0 pb-3">
                        <ExpandStrip row={row} rowKeyValue={key} activeDetail={activeDetail} onOpenDetail={onOpenDetail} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MobileCards: React.FC<{
  report: ReportData;
  columnVisibility: Record<string, boolean>;
  expandedRows: Set<string>;
  onToggleRow: (key: string) => void;
  onOpenDetail: (kind: DetailKind, row: ReportRow, rowKeyValue: string) => void;
}> = ({ report, columnVisibility, expandedRows, onToggleRow, onOpenDetail }) => {
  const columnByKey = useMemo(() => new Map(report.columns.map((column) => [column.key, column])), [report.columns]);

  return (
    <div className="space-y-3 md:hidden">
      {report.rows.map((row, index) => {
        const key = rowKey(row, index);
        const expanded = expandedRows.has(key);
        const title = formatCellValue(row[report.mobile.title], columnByKey.get(report.mobile.title));
        const subtitle = formatDate(row[report.mobile.subtitle], false);
        const totalHours = getTotalHours(row, report.mobile.metric);
        const notesCount = getNumber(row, ["notes_count", "notes_audit_count"]);
        const auditCount = getNumber(row, ["audit_count", "notes_audit_count"]);
        const violationCount = getNumber(row, ["violation_count", "geo_violation_count"]);
        const detailEntries = getMobileDetailEntries(row);
        const geoTrackingAvailable = isGeoTrackingAvailable(row);
        const visibleHourEntries = [
          hasColumn(report.columns, "regular", columnVisibility) ? `Regular ${formatCompactHours(row.regular)}` : "",
          hasColumn(report.columns, "ot", columnVisibility) ? `OT ${formatCompactHours(row.ot)}` : "",
          hasColumn(report.columns, "dt", columnVisibility) ? `DT ${formatCompactHours(row.dt)}` : "",
        ].filter(Boolean);

        return (
          <div key={key} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="px-4 pb-4 pt-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-[15px] font-semibold text-gray-900">{title}</h2>
                  <p className="mt-0.5 text-xs font-medium text-gray-500">{subtitle}</p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-lg font-bold tabular-nums text-gray-900">{formatHours(totalHours)}</div>
                  <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Total</div>
                </div>
              </div>
              <div className="mt-3.5 flex items-center gap-2 text-sm font-semibold text-gray-800">
                <span className="tabular-nums">{formatTime(row.clock_in)}</span>
                <ChevronRight className="h-4 w-4 text-gray-400" />
                <span className="tabular-nums">{formatTime(row.clock_out)}</span>
              </div>
              {visibleHourEntries.length > 0 && (
                <div className="mt-2 text-[13px] font-medium text-gray-500">
                  {visibleHourEntries.map((entry, entryIndex) => (
                    <React.Fragment key={entry}>
                      {entryIndex > 0 && <span className="mx-1 text-gray-300">•</span>}
                      {entry}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            {/* Always four actions: the row data carries the counts even when the API omits the
                matching column descriptors, so this must not be gated on hasColumn(). */}
            <div className="grid grid-cols-4 border-t border-gray-100">
              <MobileActionButton
                label="Notes"
                count={notesCount}
                tone="blue"
                disabled={notesCount === 0}
                icon={<ClipboardList className="h-5 w-5" />}
                onClick={() => onOpenDetail("notes", row, key)}
              />
              <MobileActionButton
                label="Audit"
                count={auditCount}
                tone="purple"
                disabled={auditCount === 0}
                icon={<Clock3 className="h-5 w-5" />}
                onClick={() => onOpenDetail("audit", row, key)}
              />
              <MobileActionButton
                label="Geo"
                count={violationCount}
                tone={violationCount > 0 ? "orange" : "green"}
                icon={<AlertTriangle className="h-5 w-5" />}
                onClick={() => onOpenDetail("violations", row, key)}
              />
              <MobileActionButton
                label="Track"
                tone="green"
                disabled={!geoTrackingAvailable}
                icon={<MapPin className="h-5 w-5" />}
                onClick={() => onOpenDetail("tracking", row, key)}
              />
            </div>
            {detailEntries.length > 0 && (
              <button
                type="button"
                onClick={() => onToggleRow(key)}
                className="flex h-11 w-full items-center justify-center gap-1 border-t border-gray-100 text-[13px] font-semibold text-blue-600"
              >
                {expanded ? "Hide details" : "More details"}
                <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
              </button>
            )}
            {expanded && detailEntries.length > 0 && (
              <div className="grid grid-cols-2 gap-3 border-t border-gray-100 bg-gray-50 p-4">
                {detailEntries.map(([label, value]) => (
                  <div key={label} className={label === "Latest Note" || label === "Scheduled vs Actual" ? "col-span-2" : ""}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{label}</div>
                    <div
                      className={`mt-1 text-xs font-semibold ${
                        label === "Scheduled vs Actual" && getString(value).startsWith("-") ? "text-red-600" : "text-gray-900"
                      }`}
                    >
                      {formatDetailValue(value)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PaginationControls: React.FC<{
  pagination: ReportData["pagination"];
  loading: boolean;
  onPageChange: (page: number) => void;
}> = ({ pagination, loading, onPageChange }) => {
  const start = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.total, pagination.page * pagination.limit);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
      <div>
        Showing {start}-{end} of {pagination.total}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={loading || pagination.page <= 1}
          onClick={() => onPageChange(pagination.page - 1)}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </button>
        <span className="px-2 font-medium text-gray-800">Page {pagination.page}</span>
        <button
          type="button"
          disabled={loading || !pagination.has_more}
          onClick={() => onPageChange(pagination.page + 1)}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const NoteList: React.FC<{ notes: TimecardNoteDetail[] }> = ({ notes }) => {
  if (!notes.length) return <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">No notes found for this record.</div>;

  return (
    <div className="space-y-3">
      {notes.map((note, index) => {
        const json = parseJsonObject(note.notes_json);
        const title = getString(json?.type ?? json?.title) || (index === 0 ? "Manager note" : "Employee note");
        const author = [note.first_name, note.last_name].filter(Boolean).join(" ").trim() || getString(note.created_by) || "System";
        return (
          <div key={`${note.notes_id ?? index}`} className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="text-sm font-bold text-blue-700">{normalizeText(title)}</div>
            <div className="mt-2 text-sm text-gray-950">{getString(note.notes ?? json?.notes ?? json?.message) || DASH}</div>
            <div className="mt-3 text-xs font-medium text-gray-500">
              {author} <span className="mx-1">•</span> {getString(note.created) || DASH}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AuditList: React.FC<{ audit: TimecardAuditDetail[] }> = ({ audit }) => {
  if (!audit.length) return <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">No audit events found for this record.</div>;

  return (
    <div className="relative space-y-4 pl-7 before:absolute before:left-3 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-purple-200">
      {audit.map((item, index) => {
        const logs = parseJsonObject(item.audit_logs);
        const title = getString(item.action ?? item.column_name_updated) || "Timecard updated";
        const originalValue = getString(item.column_orig_value ?? logs?.column_orig_value);
        const newValue = getString(item.column_new_value ?? logs?.column_new_value);
        const actor = getString(item.name ?? item.created_by) || "System";
        return (
          <div key={`${item.audit_id ?? index}`} className="relative rounded-lg border border-gray-200 bg-white p-4">
            <span className="absolute -left-[26px] top-4 h-3 w-3 rounded-full bg-purple-600 ring-4 ring-purple-100" />
            <div className="text-sm font-bold text-gray-950">{normalizeText(title)}</div>
            {(originalValue || newValue) && (
              <div className="mt-1 text-sm text-gray-600">
                {originalValue || DASH} <span className="mx-1">→</span> {newValue || DASH}
              </div>
            )}
            <div className="mt-2 text-xs font-medium text-gray-500">
              {actor} <span className="mx-1">•</span> {getString(item.created) || DASH}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ViolationList: React.FC<{ violations: TimecardViolationDetail[]; row: ReportRow }> = ({ violations, row }) => {
  if (!violations.length) {
    // An empty result is the normal outcome for a punch with no mobile tracking session,
    // so this is stated as information rather than styled as a failure.
    const isMobilePunch = Boolean(getTrackingUuid(row));

    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        <div className="font-semibold text-gray-900">No geo violations recorded</div>
        <p className="mt-1">
          {isMobilePunch
            ? "This punch was inside the allowed work location for every geofence check."
            : "Geofence checks only run for punches made from the mobile app. This punch has no mobile tracking session, so there is nothing to report."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {violations.map((violation, index) => {
        const lat = getString(violation.lat);
        const lng = getString(violation.lng ?? violation.long);
        const hasCoordinates = Boolean(lat && lng);
        const time =
          formatDateTimeValue(getString(violation.timestamp) || getString(violation.last_punch_time)) ||
          getString(violation.timestamp ?? violation.last_punch_time) ||
          DASH;

        return (
          <div key={`${violation.geo_fence_violation_log_id ?? index}`} className="rounded-lg border border-orange-200 bg-orange-50/40 p-4">
            <div className="flex items-start gap-2 text-orange-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="font-bold">{normalizeText(getString(violation.event_type) || "Outside work location")}</div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-x-5 gap-y-1 text-sm sm:grid-cols-2 sm:gap-y-3">
              <div className="text-gray-500">Type</div>
              <div className="font-medium text-gray-950">{normalizeText(getString(violation.event_type) || "Clock In")}</div>
              <div className="text-gray-500">Time</div>
              <div className="font-medium text-gray-950">{time}</div>
              <div className="text-gray-500">Recorded location</div>
              <div className="break-words font-medium text-gray-950">{hasCoordinates ? `${lat}, ${lng}` : DASH}</div>
              <div className="text-gray-500">Status</div>
              <div className="break-words font-medium text-gray-950">{normalizeText(getString(violation.violation_status)) || DASH}</div>
            </div>
            {hasCoordinates && (
              <button
                type="button"
                onClick={() => window.open(buildMapsUrl(lat, lng), "_blank")}
                className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-blue-200 bg-white text-sm font-bold text-blue-700"
              >
                <MapPin className="h-4 w-4" />
                View on Map
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TrackingLegend: React.FC = () => (
  <div className="absolute bottom-3 left-3 z-10 rounded-lg bg-white/95 px-3 py-2 shadow-md">
    {[
      { color: "bg-green-500", label: "Clock In" },
      { color: "bg-blue-600", label: "Tracked Point" },
      { color: "bg-red-500", label: "Clock Out" },
    ].map((item) => (
      <div key={item.label} className="flex items-center gap-2 py-0.5 text-[11px] font-medium text-gray-700">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${item.color}`} />
        {item.label}
      </div>
    ))}
  </div>
);

const TrackingPanel: React.FC<{ row: ReportRow; points: LocationPoint[]; variant?: "panel" | "fullscreen" }> = ({
  points,
  variant = "panel",
}) => {
  const isFullscreen = variant === "fullscreen";
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null);
  const validPoints = useMemo(
    () => points.filter((point) => isFiniteNumber(point.latitude) && isFiniteNumber(point.longitude)),
    [points]
  );
  const mapPath = useMemo(
    () => validPoints.map((point) => ({ lat: point.latitude, lng: point.longitude })),
    [validPoints]
  );
  // Must be referentially stable: a changing `center` identity fights panTo and snaps the map back.
  const mapCenter = useMemo(() => mapPath[0] ?? { lat: 40.7128, lng: -74.006 }, [mapPath]);
  const distanceKm = getTrackingDistanceKm(validPoints);
  const averageAccuracy =
    validPoints.length > 0
      ? validPoints.reduce((sum, point) => sum + (Number(point.accuracy) || 0), 0) / validPoints.length
      : 0;
  const { isLoaded: mapsLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  const isMobile = useIsMobileViewport();
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    setSelectedPointIndex(null);
  }, [points]);

  const handleSelectPoint = (index: number) => {
    setSelectedPointIndex(index);
    const target = mapPath[index];
    // panTo deliberately preserves the current zoom so the whole route stays in view.
    if (target && mapRef.current) mapRef.current.panTo(target);
  };

  return (
    <div className={isFullscreen ? "flex h-full min-h-0 flex-col bg-white" : "overflow-hidden rounded-lg border border-gray-200 bg-white"}>
      {validPoints.length === 0 ? (
        <div
          className={`flex flex-col items-center justify-center gap-2 bg-gray-50 p-6 text-center text-sm text-gray-500 ${
            isFullscreen ? "flex-1" : "min-h-[220px]"
          }`}
        >
          <MapPin className="h-8 w-8 text-gray-300" />
          No tracking points found in location_tracking for this punch.
        </div>
      ) : GOOGLE_MAPS_API_KEY && mapsLoaded ? (
        <div className={`relative w-full ${isFullscreen ? "min-h-[45vh] flex-1" : "h-[200px] md:h-[260px]"}`}>
          <GoogleMap
            mapContainerStyle={TRACKING_MAP_CONTAINER}
            center={mapCenter}
            zoom={14}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onUnmount={() => {
              mapRef.current = null;
            }}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              zoomControl: isFullscreen || !isMobile,
              // One finger scrolls the surrounding sheet; two fingers pan the map.
              gestureHandling: "cooperative",
            }}
          >
            <Polyline path={mapPath} options={{ strokeColor: "#2563eb", strokeOpacity: 0.9, strokeWeight: 4 }} />
            {mapPath.map((position, index) => {
              const isStart = index === 0;
              const isEnd = index === mapPath.length - 1;
              const isSelected = index === selectedPointIndex;
              const symbol = typeof window !== "undefined" ? window.google?.maps?.SymbolPath : undefined;
              const baseScale = isStart || isEnd ? 10 : isMobile ? 7 : 5;

              return (
                <Marker
                  key={`${position.lat}-${position.lng}-${index}`}
                  position={position}
                  zIndex={isSelected ? 999 : isStart || isEnd ? 10 : 1}
                  onClick={() => handleSelectPoint(index)}
                  label={
                    isStart || isEnd
                      ? { text: isStart ? "S" : "E", color: "white", fontWeight: "bold", fontSize: "11px" }
                      : undefined
                  }
                  icon={
                    symbol
                      ? {
                          path: symbol.CIRCLE,
                          scale: isSelected ? baseScale + 3 : baseScale,
                          fillColor: isStart ? "#22c55e" : isEnd ? "#ef4444" : "#2563eb",
                          fillOpacity: 1,
                          strokeColor: isSelected ? "#111827" : "white",
                          strokeWeight: isSelected ? 3 : isStart || isEnd ? 2 : 1,
                        }
                      : undefined
                  }
                />
              );
            })}
          </GoogleMap>
          {isFullscreen && <TrackingLegend />}
        </div>
      ) : (
        <div
          className={`flex items-center justify-center bg-blue-50 p-6 text-center text-sm font-medium text-blue-800 ${
            isFullscreen ? "flex-1" : "min-h-[220px]"
          }`}
        >
          {GOOGLE_MAPS_API_KEY ? "Loading tracking map..." : "Google Maps API key is not configured. Showing tracking points below."}
        </div>
      )}

      <div className="grid shrink-0 grid-cols-3 divide-x divide-gray-100 border-t border-gray-100">
        {[
          { label: "Points", value: validPoints.length },
          { label: "Distance", value: `${distanceKm.toFixed(2)} km` },
          { label: "Avg Accuracy", value: validPoints.length ? `${averageAccuracy.toFixed(0)} m` : DASH },
        ].map((item) => (
          <div key={item.label} className="px-2 py-2 sm:px-4 sm:py-3">
            <div className="text-[11px] font-medium text-gray-500 sm:text-xs">{item.label}</div>
            <div className="mt-1 text-sm font-bold text-gray-950 sm:text-base">{item.value}</div>
          </div>
        ))}
      </div>

      <div className={`divide-y divide-gray-100 overflow-y-auto ${isFullscreen ? "min-h-0 flex-1" : "max-h-72"}`}>
        {validPoints.map((point, index) => {
          const isSelected = index === selectedPointIndex;
          const pointTime = formatTrackingPointTime(point);

          return (
            <button
              type="button"
              key={point.id ?? `${point.timestamp}-${index}`}
              onClick={() => handleSelectPoint(index)}
              aria-current={isSelected ? "true" : undefined}
              aria-label={`Show tracking point at ${pointTime} on the map`}
              className={`grid w-full grid-cols-[28px_1fr] gap-3 px-4 py-3.5 text-left text-sm transition-colors ${
                isSelected ? "bg-blue-50 ring-1 ring-inset ring-blue-300" : "hover:bg-gray-50"
              }`}
            >
              <div className={`mt-1 h-3 w-3 rounded-full ${isSelected ? "bg-gray-900" : "bg-gray-400"}`} />
              <div className="min-w-0">
                <div className="font-bold text-gray-950">{pointTime}</div>
                <div className="mt-1 text-xs font-medium text-gray-500">
                  {formatCoordinate(point.latitude)}, {formatCoordinate(point.longitude)}
                  {point.accuracy != null ? ` · ±${Number(point.accuracy).toFixed(0)}m` : ""}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const MobileTrackingScreen: React.FC<{
  activeDetail: NonNullable<ActiveDetail>;
  cache: DetailCacheValue | undefined;
  onClose: () => void;
}> = ({ activeDetail, cache, onClose }) => {
  const row = activeDetail.row;
  const employee = getString(row.employee) || "Employee";
  const date = formatDate(row.date, false);
  const summary = getDetailSummary("tracking", cache);

  // The list behind a full-screen map scrolling under a finger is very noticeable.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-white">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-2 py-2">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-900"
          aria-label="Back to report"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-bold text-gray-950">Geo Tracking</h2>
          <div className="truncate text-xs font-medium text-gray-500">
            {employee} <span className="mx-1">•</span> {date}
            <span className="mx-1">•</span>
            <span className="tabular-nums">
              {formatTime(row.clock_in)} – {formatTime(row.clock_out)}
            </span>
          </div>
        </div>
        {summary && <div className="shrink-0 pr-2 text-xs font-semibold text-gray-900">{summary}</div>}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {cache?.loading ? (
          <div className="flex flex-1 items-center justify-center text-sm font-semibold text-gray-600">
            <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
            Loading geo tracking
          </div>
        ) : cache?.error ? (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{cache.error}</div>
        ) : (
          <TrackingPanel row={row} points={cache?.trackingPoints ?? []} variant="fullscreen" />
        )}
      </div>
    </div>
  );
};

const DetailContent: React.FC<{ activeDetail: ActiveDetail; cache: DetailCacheValue | undefined }> = ({ activeDetail, cache }) => {
  if (!activeDetail) return null;

  if (cache?.loading) {
    return (
      <div className="flex min-h-[180px] items-center justify-center text-sm font-semibold text-gray-600">
        <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
        Loading {getDetailTitle(activeDetail.kind).toLowerCase()}
      </div>
    );
  }

  if (cache?.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        {cache.error}
      </div>
    );
  }

  if (activeDetail.kind === "notes") return <NoteList notes={cache?.notes ?? []} />;
  if (activeDetail.kind === "audit") return <AuditList audit={cache?.audit ?? []} />;
  if (activeDetail.kind === "violations") return <ViolationList violations={cache?.violations ?? []} row={activeDetail.row} />;
  return <TrackingPanel row={activeDetail.row} points={cache?.trackingPoints ?? []} />;
};

const DetailShell: React.FC<{
  activeDetail: ActiveDetail;
  cache: DetailCacheValue | undefined;
  onClose: () => void;
}> = ({ activeDetail, cache, onClose }) => {
  if (!activeDetail) return null;
  const title = getDetailTitle(activeDetail.kind);
  const employee = getString(activeDetail.row.employee) || "Employee";
  const date = formatDate(activeDetail.row.date, false);
  const tone = activeDetail.kind === "audit" ? "purple" : activeDetail.kind === "violations" ? "orange" : activeDetail.kind === "tracking" ? "green" : "blue";
  const iconClass = {
    blue: "bg-blue-50 text-blue-700",
    purple: "bg-purple-50 text-purple-700",
    orange: "bg-orange-50 text-orange-700",
    green: "bg-green-50 text-green-700",
  }[tone];
  const icon = activeDetail.kind === "audit" ? <History className="h-4 w-4" /> : activeDetail.kind === "violations" ? <AlertTriangle className="h-4 w-4" /> : activeDetail.kind === "tracking" ? <Navigation className="h-4 w-4" /> : <FileText className="h-4 w-4" />;
  const summary = getDetailSummary(activeDetail.kind, cache);

  return (
    <>
      <div className="fixed inset-0 z-40 hidden bg-gray-950/40 md:block" onClick={onClose} />
      <div className="fixed inset-0 z-50 hidden items-center justify-center p-6 md:flex">
        <div className="max-h-[86vh] w-full max-w-2xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>{icon}</div>
              <div>
                <h2 className="text-base font-bold text-gray-950">{title}</h2>
                <div className="mt-1 text-xs font-medium text-gray-500">
                  {employee} <span className="mx-1">•</span> {date}
                </div>
                {summary && <div className="mt-1 text-xs font-semibold text-gray-900">{summary}</div>}
              </div>
            </div>
            <button type="button" onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100" aria-label="Close detail modal">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[calc(86vh-73px)] overflow-y-auto p-5">
            <DetailContent activeDetail={activeDetail} cache={cache} />
          </div>
        </div>
      </div>

      <div className="fixed inset-0 z-[55] bg-black/30 md:hidden" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-[56] max-h-[86vh] overflow-hidden rounded-t-lg bg-white shadow-2xl md:hidden">
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-gray-300" />
        <div className="flex items-start justify-between px-5 pb-3 pt-4">
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>{icon}</div>
            <div>
              <h2 className="text-lg font-bold text-gray-950">{title}</h2>
              <div className="mt-1 text-xs font-semibold text-gray-600">
                {employee} <span className="mx-1">•</span> {date}
              </div>
              {summary && <div className="mt-1 text-xs font-semibold text-gray-900">{summary}</div>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500" aria-label="Close detail sheet">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 pb-6">
          <DetailContent activeDetail={activeDetail} cache={cache} />
        </div>
      </div>
    </>
  );
};

export const Reports = () => {
  const dispatch = useAppDispatch();
  const { sites, departments } = useSelector((state: RootState) => state.common);

  const [draftFilters, setDraftFilters] = useState<DraftFilters>(() => emptyFilters());
  const [appliedFilters, setAppliedFilters] = useState<DraftFilters>(() => emptyFilters());
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const isMobile = useIsMobileViewport();
  const [activeDetail, setActiveDetail] = useState<ActiveDetail>(null);
  const [detailCache, setDetailCache] = useState<Record<string, DetailCacheValue>>({});

  const siteOptions = useMemo(() => mapSitesToOptions(sites), [sites]);
  const departmentOptions = useMemo(() => mapDepartmentsToOptions(departments), [departments]);

  useEffect(() => {
    dispatch(getSites());
    dispatch(getDepartments());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setLoading(true);
      setLoadError(null);
      setValidationMessage(null);

      const params: TimecardReportParams = {
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
        site_id: appliedFilters.site_id,
        department_codes: appliedFilters.department_codes,
        search: appliedFilters.search,
        page,
        limit: PAGE_LIMIT,
      };

      try {
        const response = await runTimecardReport(params);
        if (cancelled) return;

        if (!response.status) {
          setValidationMessage(response.message || "Please check the report filters.");
          return;
        }

        setReport(response.data ?? null);
        setExpandedRows(new Set());
        setActiveDetail(null);
      } catch (error: any) {
        if (cancelled) return;
        setLoadError(error?.message || "Unexpected server error while loading the report.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [appliedFilters, page]);

  useEffect(() => {
    if (!report?.columns.length) return;

    setColumnVisibility((previous) => {
      const next: Record<string, boolean> = {};
      report.columns.forEach((column) => {
        next[column.key] = column.toggleable ? previous[column.key] ?? column.visible : true;
      });
      return next;
    });
  }, [report?.columns]);

  const reportFilters = report?.filters?.length ? report.filters : DEFAULT_REPORT_FILTERS;

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...draftFilters });
    setMobileFiltersOpen(false);
  };

  const changePage = (nextPage: number) => {
    setPage(nextPage);
  };

  const toggleColumn = (key: string) => {
    setColumnVisibility((current) => ({ ...current, [key]: !current[key] }));
  };

  const toggleRow = (key: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const retry = () => setAppliedFilters((current) => ({ ...current }));

  const openDetail = async (kind: DetailKind, row: ReportRow, rowKeyValue: string) => {
    if (kind === "tracking" && !isGeoTrackingAvailable(row)) return;

    const cacheKey = detailCacheKey(rowKeyValue, kind);

    setActiveDetail({ kind, row, rowKey: rowKeyValue });

    const cached = detailCache[cacheKey];
    if (cached?.status === "loading") return;
    if (cached?.status === "loaded") return;

    if (kind === "tracking") {
      const trackingUuid = getTrackingUuid(row);

      if (!trackingUuid) {
        setDetailCache((current) => ({
          ...current,
          [cacheKey]: { status: "error", loading: false, error: "This report row does not include a tracking UUID." },
        }));
        return;
      }

      setDetailCache((current) => ({
        ...current,
        [cacheKey]: { status: "loading", loading: true, error: null },
      }));

      try {
        const trackingPoints = await TrackingSessionService.listLocationPoints(trackingUuid);
        setDetailCache((current) => ({
          ...current,
          [cacheKey]: { status: "loaded", loading: false, error: null, trackingPoints },
        }));
      } catch (error: any) {
        setDetailCache((current) => ({
          ...current,
          [cacheKey]: { status: "error", loading: false, error: error?.message || "Unable to load tracking points." },
        }));
      }
      return;
    }

    const recordId = getRecordId(row);

    if (!recordId) {
      const availableKeys = Object.keys(row).join(", ");
      console.warn("[Reports] No timecard record id on row for detail kind", kind, "— row keys:", Object.keys(row), row);
      setDetailCache((current) => ({
        ...current,
        [cacheKey]: {
          status: "error",
          loading: false,
          error: `This report row has no timecard record id, so ${getDetailTitle(kind).toLowerCase()} cannot be loaded. Fields present: ${availableKeys}`,
        },
      }));
      return;
    }

    setDetailCache((current) => ({
      ...current,
      [cacheKey]: { status: "loading", loading: true, error: null },
    }));

    const payload = { id: recordId, employee_id: getEmployeeId(row) };

    try {
      if (kind === "notes") {
        const response = await getTimecardReportNotes(payload);
        setDetailCache((current) => ({
          ...current,
          [cacheKey]: response.status
            ? { status: "loaded", loading: false, error: null, notes: response.data ?? [] }
            : { status: "error", loading: false, error: response.message || "Unable to load notes." },
        }));
        return;
      }

      if (kind === "audit") {
        const response = await getTimecardReportAudit(payload);
        setDetailCache((current) => ({
          ...current,
          [cacheKey]: response.status
            ? { status: "loaded", loading: false, error: null, audit: response.data ?? [] }
            : { status: "error", loading: false, error: response.message || "Unable to load audit history." },
        }));
        return;
      }

      const response = await getTimecardReportViolations(payload);
      setDetailCache((current) => ({
        ...current,
        [cacheKey]: response.status
          ? { status: "loaded", loading: false, error: null, violations: response.data ?? [] }
          : { status: "error", loading: false, error: response.message || "Unable to load geo details." },
      }));
    } catch (error: any) {
      setDetailCache((current) => ({
        ...current,
        [cacheKey]: { status: "error", loading: false, error: error?.message || "Unable to load this detail view." },
      }));
    }
  };

  const activeCache = activeDetail ? detailCache[detailCacheKey(activeDetail.rowKey, activeDetail.kind)] : undefined;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1680px] rounded-lg border border-gray-200 bg-white/70 p-4 shadow-sm md:p-6">
        <DesktopHeader
          report={report}
          draftFilters={draftFilters}
          filters={reportFilters}
          siteOptions={siteOptions}
          departmentOptions={departmentOptions}
          columnVisibility={columnVisibility}
          loading={loading}
          validationMessage={validationMessage}
          onDraftChange={setDraftFilters}
          onSubmit={applyFilters}
          onToggleColumn={toggleColumn}
        />

        <MobileHeader
          report={report}
          draftFilters={draftFilters}
          filters={reportFilters}
          siteOptions={siteOptions}
          departmentOptions={departmentOptions}
          columnVisibility={columnVisibility}
          filterOpen={mobileFiltersOpen}
          loading={loading}
          validationMessage={validationMessage}
          onDraftChange={setDraftFilters}
          onSubmit={applyFilters}
          onToggleColumn={toggleColumn}
          onToggleFilter={() => setMobileFiltersOpen((open) => !open)}
        />

        <div className="mt-4 space-y-4">
          {loadError ? (
            <ErrorState message={loadError} onRetry={retry} />
          ) : loading && !report ? (
            <LoadingState />
          ) : report && report.layout !== "table" ? (
            <UnsupportedLayoutState layout={report.layout} />
          ) : report && report.rows.length === 0 ? (
            <EmptyState />
          ) : report ? (
            <>
              <DesktopTable
                report={report}
                columnVisibility={columnVisibility}
                expandedRows={expandedRows}
                activeDetail={activeDetail}
                onToggleRow={toggleRow}
                onOpenDetail={openDetail}
              />
              <MobileCards
                report={report}
                columnVisibility={columnVisibility}
                expandedRows={expandedRows}
                onToggleRow={toggleRow}
                onOpenDetail={openDetail}
              />
              {/* Rendered exclusively: DetailShell mounts both the desktop modal and the mobile
                  sheet, so a CSS-only split would mount two <GoogleMap> instances for tracking. */}
              {isMobile && activeDetail?.kind === "tracking" ? (
                <MobileTrackingScreen activeDetail={activeDetail} cache={activeCache} onClose={() => setActiveDetail(null)} />
              ) : (
                <DetailShell activeDetail={activeDetail} cache={activeCache} onClose={() => setActiveDetail(null)} />
              )}
              <PaginationControls pagination={report.pagination} loading={loading} onPageChange={changePage} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
